import userflow from 'userflow.js'
import {
  isResourceCenterActive,
  releaseResourceCenterCorner,
  setResourceCenterCornerYielded
} from '../components/common/ResourceCenter/resourceCenterStore'

export interface SetUserflowVisibleOptions {
  /**
   * Re-open the resource center panel, not just its launcher. Defaults to `true`.
   *
   * HubSpot chat passes `false`; it must keep doing so with either resource center. Every resource-center open re-runs Userflow's own HubSpot
   * integration, which calls `HubSpotConversations.widget.hide()`, so re-opening the panel right
   * after a chat close feeds the loop that ENG-1461 is about. It also pops the help panel open
   * with no user action.
   */
  reopenResourceCenter?: boolean
}

/**
 * Hands the bottom-right corner back and forth between Userflow's resource center and a
 * third-party widget that lives in the same spot (HubSpot chat, the GitBook docs embed).
 *
 * Call with `false` before showing the other widget, and `true` once it closes so the user
 * lands back on the Userflow resource center they opened it from.
 *
 * With the in-house resource center mounted (`resourceCenterNext`, #25353), the same calls move
 * that one instead, and the Userflow launcher stays hidden whatever `visible` says. `true` re-opens
 * the in-house panel only when the panel opened the widget that is closing; docs opened from a help
 * link elsewhere close back to the launcher. The in-house
 * panel has no HubSpot integration of its own, so re-opening it cannot hide the chat; chat still
 * passes `reopenResourceCenter: false` so that closing the chat never pops the panel open.
 */
export function setUserflowVisible(visible: boolean, opts: SetUserflowVisibleOptions = {}) {
  const { reopenResourceCenter = true } = opts
  if (isResourceCenterActive()) {
    hideUserflowResourceCenter()
    if (visible) {
      // Re-opens only when the panel opened the widget that is closing (`handOffFromResourceCenter`).
      releaseResourceCenterCorner({ reopen: reopenResourceCenter })
    } else {
      setResourceCenterCornerYielded(true)
    }
    return
  }
  try {
    userflow.setResourceCenterLauncherHidden(!visible)
    if (!visible) {
      userflow.closeResourceCenter()
    } else if (reopenResourceCenter) {
      userflow.openResourceCenter()
    }
  } catch {
    // Fallback to CSS if userflow not initialized
    const el = document.getElementById('userflow-ui')
    if (el) el.style.display = visible ? '' : 'none'
  }
}

/** Keeps the Userflow launcher and panel hidden while the in-house resource center owns the corner. */
export function hideUserflowResourceCenter() {
  try {
    userflow.setResourceCenterLauncherHidden(true)
    userflow.closeResourceCenter()
  } catch {
    // Userflow not initialized: nothing to hide. Do not hide `#userflow-ui` as the fallback above
    // does, because Userflow flows and surveys still render there until #25355.
  }
}
